'use client';

import type { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { usePathname } from 'next/navigation';
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { loadCustomerIoCdp } from '@/lib/analytics/providers/customerio/client';
import { env } from '@/lib/config/env';
// Both imported from their own modules rather than the `@/lib/network`
// barrel, which reaches `mainnet-enabled.ts` → `@/lib/analytics` and would
// close an import cycle.
import { useNetworkStore } from '@/lib/network/store';
import type { Network } from '@/lib/network/types';

/**
 * Mounts the Customer.io CDP browser client and feeds it page views + logged-in
 * identity, so Journeys profiles carry on-site behaviour alongside the waitlist
 * attributes the backend syncs via the Track API. Also registers the in-app
 * messaging plugin so modals/banners/tooltips authored in Customer.io render
 * for identified users.
 *
 * - No write key configured: renders nothing and loads no script (the deploy
 *   stays inert until NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY is set).
 * - Non-production network: `loadCustomerIoCdp` returns null, so nothing here
 *   runs. A local session must not reach the production CRM — not even the
 *   page call the SDK fires automatically on load.
 * - Anonymous visitors: page views only, under the SDK's own anonymous id.
 * - Logged-in users: `identify` with the Morfi user id, so the CDP profile
 *   merges into the same person the backend identifies.
 * - Route changes: an explicit `page()` per pathname — Next's App Router does
 *   client-side navigation, so the SDK's automatic first-load page call would
 *   otherwise be the only one recorded for the whole session.
 * - Custom product events are NOT tracked here: the analytics composite
 *   mirrors every typed event from `src/events` into the SAME shared client
 *   via the Customer.io sink (`lib/analytics/providers/customerio`).
 *
 * The write key and in-app site id are public by design (they ship in client
 * JS). The write key only permits writes into this one CDP source; the site id
 * alone cannot authenticate Track API writes (that needs the server-only key).
 * The Track API *key* must never appear here.
 */

/**
 * Register Customer.io in-app messaging on an already-loaded analytics client.
 *
 * A MODULE-LEVEL helper on purpose, for two independent reasons:
 *  1. SSR safety — `InAppPlugin` statically pulls in `customerio-gist-web`,
 *     which touches `HTMLElement`/custom-elements at module-evaluation time.
 *     Importing it dynamically keeps it out of the SSR module graph, so
 *     prerender no longer crashes with `ReferenceError: HTMLElement is not
 *     defined`. The static `AnalyticsBrowser` import tree-shakes gist-web out.
 *  2. React Compiler — an inline `import()` inside the component body makes the
 *     compiler bail on optimizing the whole provider ("Handle Import
 *     expressions"). Keeping the import expression out here lets the compiler
 *     still memoize `CustomerIoProvider`.
 */
async function registerInAppMessaging(analytics: AnalyticsBrowser, siteId: string): Promise<void> {
  const { InAppPlugin } = await import('@customerio/cdp-analytics-browser');
  // `anonymousInApp: false` shows messages only to identified users, matching
  // the identify-only posture of the rest of this provider. The plugin handles
  // built-in click behaviours (dismiss, open-URL) itself — no event wiring.
  await analytics.register(
    InAppPlugin({
      siteId,
      events: null,
      _env: undefined,
      _logging: undefined,
      anonymousInApp: false,
      colorScheme: 'auto',
    }),
  );
}

export function CustomerIoProvider(): JSX.Element | null {
  const { user, isAuthenticated } = useAuth();
  const pathname = usePathname();
  // Re-runs the load effect when the user toggles network, so a session that
  // starts on local still picks the client up on switching to devnet/mainnet.
  const network = useNetworkStore((state): Network => state.current);
  const analyticsRef = useRef<AnalyticsBrowser | null>(null);
  const lastIdentityKey = useRef<string>('');

  // Load once on mount. Store the client itself, not the load promise — the
  // browser client is thenable, so awaiting it can unwrap to something without
  // the buffered page/identify/track methods. The client comes from the shared
  // singleton so the analytics event sink writes through the same instance.
  useEffect(() => {
    const writeKey = env.NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY;
    if (writeKey === undefined || writeKey === '') return;
    const analytics = loadCustomerIoCdp(writeKey, network);
    if (analytics === null) return;
    analyticsRef.current = analytics;

    // In-app messaging — gated independently on the site id so it can roll out
    // separately from CDP tracking. Delegated to a module-level helper (see its
    // doc comment for the SSR + React-Compiler reasons it lives outside here).
    const siteId = env.NEXT_PUBLIC_CUSTOMERIO_SITE_ID;
    if (siteId !== undefined && siteId !== '') {
      void registerInAppMessaging(analytics, siteId);
    }
  }, [network]);

  // One page call per route change (plus the initial load).
  useEffect(() => {
    const analytics = analyticsRef.current;
    if (analytics === null) return;
    // Pass the path explicitly rather than letting the SDK read
    // window.location: on an App Router client navigation the effect can run
    // before the URL settles, and it also makes `pathname` a genuine
    // dependency rather than a bare re-run trigger.
    void analytics.page({ path: pathname });
  }, [pathname]);

  // Tie the CDP profile to the Morfi user id once authenticated.
  useEffect(() => {
    const analytics = analyticsRef.current;
    if (analytics === null) return;

    if (!isAuthenticated || user === null) {
      // Only on the logged-in → logged-out transition. `reset()` clears the
      // persisted user id and anonymous id: without it the SDK keeps
      // attributing this browser's events to the previous user, so anything
      // before the next `identify` — including another person's page views on
      // a shared device — lands on the wrong profile.
      if (lastIdentityKey.current !== '') {
        void analytics.reset();
        lastIdentityKey.current = '';
      }
      return;
    }
    if (user.id === lastIdentityKey.current) return;
    lastIdentityKey.current = user.id;

    const traits: Record<string, string> = {};
    if (user.email !== '') traits.email = user.email;
    if (user.displayName !== null) traits.name = user.displayName;

    void analytics.identify(user.id, traits);
  }, [isAuthenticated, user]);

  return null;
}
